import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, PanelLeft, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Avatar } from '@/components/ui/Avatar';
import { ParaconLogo } from '@/components/brand/ParaconLogo';
import { homePathFor, needsProjectSelector, sidebarItemFor, sidebarNavItems } from '@/nav/navigation';
import { useAuth } from '@/features/auth/AuthContext';
import { ProjectSelector, type ProjectOption } from './ProjectSelector';

/**
 * Expanded 288px sidebar. Doubles as the off-canvas drawer below `lg`, where
 * `variant="drawer"` swaps the collapse control for a close button.
 *
 * Flat list — no dropdowns. Every item is a direct link.
 */
export function Sidebar({
  onCollapse,
  onClose,
  variant = 'fixed',
  user,
  projects,
  currentProjectId,
  onProjectChange,
}: {
  onCollapse?: () => void;
  onClose?: () => void;
  variant?: 'fixed' | 'drawer';
  user: { name: string; roleLabel: string; avatarUrl?: string | null };
  projects: ProjectOption[];
  currentProjectId: string;
  onProjectChange: (id: string) => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const activeItem = sidebarItemFor(location.pathname);
  const { user: authUser } = useAuth();
  const access = {
    feedback: Boolean(authUser?.feedback_access),
    executive: Boolean(authUser?.executive_access),
    finance: Boolean(authUser?.finance_access),
  };
  // One flat list — the client struck the section headings (round 6).
  const navItems = sidebarNavItems(access);

  return (
    <aside
      className={cn(
        'flex w-sidebar shrink-0 flex-col bg-navy',
        variant === 'fixed' ? 'fixed inset-y-0 left-0 z-30 border-r border-white/[0.06]' : 'h-full',
      )}
      aria-label="Primary navigation"
    >
      <div className="flex h-topbar shrink-0 items-center justify-between gap-2 px-5">
        <button
          type="button"
          onClick={() => navigate(homePathFor(access))}
          aria-label="OneParacon home"
          className="rounded transition-opacity hover:opacity-80"
        >
          <ParaconLogo src={authUser?.company_logo_url} />
        </button>
        {variant === 'drawer' ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            className="flex h-9 w-9 items-center justify-center rounded-lg text-white/60 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X aria-hidden className="h-5 w-5" />
          </button>
        ) : (
          <button
            type="button"
            onClick={onCollapse}
            aria-label="Collapse navigation"
            className="flex h-9 w-9 items-center justify-center rounded-lg text-white/60 transition-colors hover:bg-white/10 hover:text-white"
          >
            <PanelLeft aria-hidden className="h-[18px] w-[18px]" />
          </button>
        )}
      </div>

      {(
        <div className="px-4 pb-4">
          {needsProjectSelector(location.pathname) && (
            <ProjectSelector projects={projects} currentId={currentProjectId} onChange={onProjectChange} />
          )}
        </div>
      )}

      <nav className="flex-1 overflow-y-auto px-3 pb-4 scrollbar-slim-dark">
        {(
          <div>
            <ul className="space-y-0.5">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = activeItem?.id === item.id;

                return (
                  <li key={item.id}>
                    <NavLink
                      to={item.path}
                      onClick={onClose}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'relative flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors',
                        active
                          ? 'bg-white/[0.12] font-semibold text-white'
                          : 'text-white/75 hover:bg-white/[0.07] hover:text-white',
                      )}
                    >
                      {/* Accent bar marking the active item, per the approved sidebar. */}
                      {active && (
                        <span
                          aria-hidden
                          className="absolute inset-y-1.5 left-0 w-0.5 rounded-full bg-accent-400"
                        />
                      )}
                      <Icon aria-hidden className="h-[18px] w-[18px] shrink-0" />
                      <span className="min-w-0 flex-1 truncate text-left">{item.label}</span>
                      {item.attention && (
                        <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-gold-500" />
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </nav>

      <div className="shrink-0 border-t border-white/[0.07] px-3 py-3">
        <button
          type="button"
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-white/[0.07]"
        >
          <Avatar name={user.name} src={user.avatarUrl} size="sm" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-white">{user.name}</span>
            <span className="block truncate text-2xs text-white/50">{user.roleLabel}</span>
          </span>
          <ChevronDown aria-hidden className="h-4 w-4 shrink-0 text-white/50" />
        </button>
      </div>
    </aside>
  );
}
